import { useAtomValue } from "jotai";
import { CheckCircle2, Copy, Download, Link2, Share2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import whiteTick from "@/assets/Dashboard/white-tick.svg";
import { certificates } from "@/api/endpoints/certificates";
import { navbarHeightAtom, userAtom } from "@/store/atoms";

const SHARE_URL = "https://app.urfx.io.com/certificates/verify/48b1c34v1-da2a-4c91-a8a6-9d30786f1c36";

export default function CertificateDetail() {
  const { t } = useTranslation();
  const navbarHeight = useAtomValue(navbarHeightAtom);
  const [isCopied, setIsCopied] = useState(false);
  const user = useAtomValue(userAtom);
  const { id } = useParams<{ id: string }>();
  const certificateQuery = useQuery({
    queryKey: ["user-certificate", user?.id, id],
    queryFn: async () => {
      const response = await certificates.getUserCertificates(Number.parseInt(user!.id, 10));
      return response.data.certificates.find((certificate) => String(certificate.id) === id) ?? null;
    },
    enabled: Boolean(user?.id && id),
  });
  const certificate = certificateQuery.data;

  const downloadCertificate = () => {
    if (certificate?.certificateUrl) {
      window.open(certificate.certificateUrl, "_blank", "noopener,noreferrer");
    }
  };

  const copyShareLink = async () => {
    await navigator.clipboard.writeText(SHARE_URL);
    setIsCopied(true);
    window.setTimeout(() => setIsCopied(false), 1800);
  };

  return (
    <main
      style={{ height: `calc(100vh - ${navbarHeight}px)` }}
      className="w-full overflow-hidden bg-[#070707] px-3 py-4 text-white sm:px-5 sm:py-6"
    >
      <div className="hide-scrollbar mx-auto h-full max-w-[1600px] overflow-y-auto pb-8">
        <div className="mb-5 flex flex-col gap-4 rounded-3xl border border-white/10 bg-white/[0.035] p-5 sm:p-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex size-11 items-center justify-center rounded-2xl border border-emerald-300/20 bg-emerald-300/10">
              <CheckCircle2 className="size-6 text-emerald-200" />
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-emerald-200/60">{t("Verified achievement")}</p>
              <h1 className="mt-1 text-lg font-semibold sm:text-xl">{t("The authenticity of this certificate is successfully verified.")}</h1>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button type="button" onClick={downloadCertificate} disabled={!certificate?.certificateUrl || certificateQuery.isLoading} className="btn-gradient-left rounded-xl px-4 py-2.5 text-sm font-medium disabled:cursor-wait disabled:opacity-60">
              {certificateQuery.isLoading ? t("Loading...") : t("Open Certificate")}
              <Download className="ml-2 size-4" />
            </button>
          </div>
        </div>

        <section className="mb-5 rounded-3xl border border-white/10 bg-white/[0.035] p-5 sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-xl bg-cyan-300/10 text-cyan-100"><Share2 className="size-5" /></div>
              <div>
                <h2 className="text-lg font-semibold">{t("Share this page with others")}</h2>
                <p className="mt-1 text-sm text-white/40">{t("Let others verify this achievement.")}</p>
              </div>
            </div>
            <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-white/65 sm:min-w-[420px]">
                <Link2 className="size-4 shrink-0 text-cyan-100/70" />
                <span className="truncate">{SHARE_URL}</span>
              </div>
              <button type="button" onClick={copyShareLink} className="btn-gradient-left rounded-xl px-4 py-2 text-sm font-medium">
                <Copy className="mr-2 size-4" />
                {isCopied ? t("Copied") : t("Copy")}
              </button>
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-white/10 bg-white/[0.035] p-3 shadow-[0_20px_80px_rgba(0,0,0,0.3)] sm:p-5">
          <div className="mb-4 flex items-center justify-between px-1">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-white/35">{t("Certificate preview")}</p>
              <h2 className="mt-1 text-lg font-semibold">{t("Your achievement")}</h2>
            </div>
            <img src={whiteTick} alt="verified" className="size-7 opacity-80" />
          </div>
          <div className="mx-auto flex min-h-[360px] w-full max-w-[1600px] items-center justify-center overflow-hidden rounded-2xl bg-[#101214] p-3">
            {certificateQuery.isLoading ? (
              <p className="text-sm text-white/50">Loading certificate...</p>
            ) : certificateQuery.isError || !certificate?.certificateUrl ? (
              <p className="text-sm text-white/50">Certificate unavailable.</p>
            ) : (
              <img src={certificate.certificateUrl} alt="Certificate" className="max-h-[75vh] w-full object-contain" />
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
